// Jersey painter: a single layered rendering (contracts JerseyDesign) for the preview, the shop and the 3D texture.
// SVG paths only go through Path2D (pure geometry, validated by the schema): no tag is interpreted.
import type { JerseyDesign, JerseyLayer, Look } from '@gruvetto/contracts';
import { resolveLook } from '@gruvetto/creations';

const S = 256;

function shade(hex: string, f: number): string {
  const n = parseInt(hex.slice(1), 16);
  const c = [n >> 16, (n >> 8) & 255, n & 255].map(v => Math.round(f < 1 ? v * f : v + (255 - v) * (f - 1)));
  return '#' + c.map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('');
}
const safeHex = (c: string) => (/^#[0-9a-f]{6}$/i.test(c) ? c : '#ffd400');
/** Actual colour of a paint, depending on the rider's team colour. */
export function paint(p: string, team: string): string {
  const t = safeHex(team);
  return p === 'team' ? t : p === 'team-dark' ? shade(t, 0.55) : p === 'team-light' ? shade(t, 1.45) : p === 'white' ? '#f4f4f4' : p === 'black' ? '#141518' : safeHex(p);
}

const SHAPES: Record<string, (g: CanvasRenderingContext2D) => void> = {
  circle: g => g.arc(0, 0, 0.5, 0, Math.PI * 2),
  diamond: g => { g.moveTo(0, -0.5); g.lineTo(0.38, 0); g.lineTo(0, 0.5); g.lineTo(-0.38, 0); },
  triangle: g => { g.moveTo(0, -0.5); g.lineTo(0.45, 0.4); g.lineTo(-0.45, 0.4); },
  star: g => { for (let i = 0; i < 10; i++) { const r = i % 2 ? 0.22 : 0.5, a = (i * Math.PI) / 5 - Math.PI / 2; g.lineTo(Math.cos(a) * r, Math.sin(a) * r); } },
  bolt: g => { g.moveTo(0.08, -0.5); g.lineTo(-0.3, 0.06); g.lineTo(-0.02, 0.06); g.lineTo(-0.12, 0.5); g.lineTo(0.3, -0.08); g.lineTo(0.02, -0.08); },
  heart: g => { g.moveTo(0, 0.42); g.bezierCurveTo(-0.6, 0, -0.35, -0.5, 0, -0.2); g.bezierCurveTo(0.35, -0.5, 0.6, 0, 0, 0.42); },
  flame: g => { g.moveTo(0, -0.5); g.bezierCurveTo(0.35, -0.1, 0.4, 0.2, 0.2, 0.45); g.bezierCurveTo(0.1, 0.5, -0.1, 0.5, -0.2, 0.45); g.bezierCurveTo(-0.4, 0.2, -0.2, -0.1, 0, -0.5); },
  wheel: g => { g.arc(0, 0, 0.5, 0, Math.PI * 2); g.moveTo(0.18, 0); g.arc(0, 0, 0.18, 0, Math.PI * 2, true); },
};
const FONTS = { sans: 'system-ui, sans-serif', serif: 'Georgia, serif', mono: 'ui-monospace, Menlo, monospace', display: '"Inter Variable", Impact, system-ui, sans-serif' };

function layer(g: CanvasRenderingContext2D, l: JerseyLayer, team: string, rnd: () => number) {
  const col = 'color' in l ? paint(l.color, team) : '#000';
  g.save();
  switch (l.type) {
    case 'gradient': {
      const a = (l.angle * Math.PI) / 180, dx = Math.sin(a) * S / 2, dy = Math.cos(a) * S / 2;
      const gr = g.createLinearGradient(S / 2 - dx, S / 2 + dy, S / 2 + dx, S / 2 - dy);
      gr.addColorStop(0, 'rgba(0,0,0,0)'); gr.addColorStop(1, col); g.fillStyle = gr; g.fillRect(0, 0, S, S); break;
    }
    case 'stripes': {
      const period = S / l.count, w = period * l.width, off = (l.offset || 0) * period;
      g.fillStyle = col;
      if (l.dir === 'd') { g.translate(S / 2, S / 2); g.rotate(Math.PI / 4); for (let i = -l.count * 2; i < l.count * 2; i++) g.fillRect(i * period + off - S, -S, w, S * 2); }
      else for (let i = -1; i <= l.count; i++) { const p = i * period + off; if (l.dir === 'h') g.fillRect(0, p, S, w); else g.fillRect(p, 0, w, S); }
      break;
    }
    case 'band': { g.fillStyle = col; const c = l.pos * S, w = l.size * S; if (l.dir === 'h') g.fillRect(0, c - w / 2, S, w); else g.fillRect(c - w / 2, 0, w, S); break; }
    case 'dots': {
      g.fillStyle = col; const r = (l.size * l.cell) / 2;
      for (let y = 0, row = 0; y < S + l.cell; y += l.cell, row++) for (let x = 0; x < S + l.cell; x += l.cell) { g.beginPath(); g.arc(x + l.cell / 2 + (l.stagger && row % 2 ? l.cell / 2 : 0), y + l.cell / 2, r, 0, Math.PI * 2); g.fill(); }
      break;
    }
    case 'checker': { g.fillStyle = col; for (let y = 0; y < S / l.cell; y++) for (let x = 0; x < S / l.cell; x++) if ((x + y) % 2) g.fillRect(x * l.cell, y * l.cell, l.cell, l.cell); break; }
    case 'chevrons': {
      g.strokeStyle = col; g.lineWidth = (l.width * S) / l.count; const step = S / l.count;
      for (let i = 0; i < l.count; i++) { const y = step * (i + 0.5); g.beginPath(); g.moveTo(0, y - step * 0.35); g.lineTo(S / 2, y + step * 0.15); g.lineTo(S, y - step * 0.35); g.stroke(); }
      break;
    }
    case 'text': {
      g.fillStyle = col; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.font = `${l.bold ? 900 : 600} ${Math.round(l.size * S)}px ${FONTS[l.font || 'sans']}`;
      g.fillText(l.text, l.x * S, l.y * S, S * 0.95); break;
    }
    case 'shape': {
      g.fillStyle = col; g.translate(l.x * S, l.y * S); g.rotate(((l.rotate || 0) * Math.PI) / 180); g.scale(l.size * S, l.size * S);
      g.beginPath(); SHAPES[l.shape](g); g.closePath(); g.fill('evenodd'); break;
    }
    case 'path': {
      let path: Path2D;
      try { path = new Path2D(l.d); } catch { break; }
      const k = (l.size * S) / l.view;
      g.fillStyle = col; g.translate(l.x * S - (l.view * k) / 2, l.y * S - (l.view * k) / 2); g.scale(k, k); g.fill(path); break;
    }
    case 'noise': { for (let i = 0; i < 2000; i++) { g.fillStyle = `rgba(0,0,0,${rnd() * l.amount})`; g.fillRect(rnd() * S, rnd() * S, 2, 2); } break; }
  }
  g.restore();
}

/** Paints a jersey on a square 256 px 2D context. */
export function drawJersey(g: CanvasRenderingContext2D, d: JerseyDesign, team: string) {
  let seed = 7;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  g.fillStyle = paint(d.base, team); g.fillRect(0, 0, S, S);
  for (const l of d.layers) layer(g, l, team, rnd);
}

/** Worn jersey: created (embedded in the outfit) or official pattern. */
export function lookDesign(look: Partial<Look> | null | undefined): JerseyDesign {
  return resolveLook(look).jersey;
}

const urls = new Map<string, string>();
/** Jersey image (data URL), cached by pattern and colour. */
export function jerseyDataUrl(d: JerseyDesign, team: string): string {
  const key = team + JSON.stringify(d);
  let u = urls.get(key);
  if (!u) {
    const c = document.createElement('canvas'); c.width = c.height = S;
    drawJersey(c.getContext('2d')!, d, team);
    u = c.toDataURL('image/png');
    if (urls.size > 200) urls.delete(urls.keys().next().value!);
    urls.set(key, u);
  }
  return u;
}
